import { useCallback, useEffect, useState } from 'react'

import { useLazyQuery } from '@apollo/client'
import { useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'
import { Link, Send } from 'lucide-react'

import { useParams, navigate } from '@redwoodjs/router'
import { useMutation } from '@redwoodjs/web'
import { MetaTags } from '@redwoodjs/web'
import { useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import {
  DatePreset,
  DateRangeValue,
} from 'src/components/Inputs/DateFilter/DateFilter'
import { SearchBox } from 'src/components/Inputs/SearchBox/SearchBox'
import ToggleButtonGroup, {
  ToggleOption,
} from 'src/components/Inputs/ToggleButtonGroup/ToggleButtonGroup'
import ActionsMenu from 'src/components/Overlays/ActionsMenu/ActionsMenu'
import EmailSenderModal from 'src/components/Overlays/EmailSenderModal/EmailSenderModal'
import FiltersMenu, {
  FiltersMenuFilter,
} from 'src/components/Overlays/FiltersMenu/FiltersMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_COMPANY } from 'src/graphql/companies.graphql'
import { GET_EMPLOYEES_ONLY } from 'src/graphql/employees.graphql'
import { ApplicantStage, HiringStage } from 'src/graphql/types/applicants'
import { VoEStatus, VoEType } from 'src/graphql/types/verificationOfEmployments'
import {
  GET_VOE_LIST,
  GET_VOE_TAB_COUNTS,
  SEND_INSTRUCTIONS_EMAIL,
} from 'src/graphql/voe.graphql'
import IconChevronDown from 'src/icons/IconChevronDown'
import { formatInternationalPhoneNumber } from 'src/lib/phone.utils'
import {
  DEFAULT_VOE_VERIFICATION_PERIOD_MONTHS,
  formatVerificationPeriod,
} from 'src/lib/voeVerificationPeriod'

import OutboundVoETable from './components/OutboundVoETable/OutboundVoETable'
import VoERequestsTable from './components/VoERequestsTable/VoERequestsTable'
import { loadFiltersFromUrl } from './loadFiltersFromUrl'
import {
  NEXT_ACTION_FILTER_OPTIONS,
  buildNextActionFilters,
} from './nextActionFilter'
import {
  RESPONSE_FILTER_OPTIONS,
  buildResponseAutonomyFilters,
} from './responseAutonomyFilter'
import { updateFiltersInUrl } from './updateFiltersInUrl'

const LIMIT = 20

const ALL_VOE_STATUSES = [
  { label: 'Not Started', value: VoEStatus.NOT_STARTED },
  { label: 'In Progress', value: VoEStatus.IN_PROGRESS },
  { label: 'Pending Review', value: VoEStatus.PENDING_REVIEW },
  { label: 'Completed', value: VoEStatus.COMPLETED },
  { label: 'Canceled', value: VoEStatus.CANCELED },
  { label: 'Declined', value: VoEStatus.DECLINED },
]

const INBOUND_STATUSES = [
  { label: 'Pending review', value: VoEStatus.PENDING_REVIEW },
  { label: 'Completed', value: VoEStatus.COMPLETED },
]

const APPLICANT_STAGE_OPTIONS = [
  { label: 'Prospect', value: 'PROSPECT' },
  { label: 'Hired', value: 'HIRED' },
  { label: 'Terminated', value: 'TERMINATED' },
  { label: 'Not Interested', value: 'NOT_INTERESTED' },
  { label: 'Disqualified', value: 'DISQUALIFIED' },
]

// Default outbound statuses exclude Completed, Canceled, and Declined
const DEFAULT_OUTBOUND_STATUSES = [
  VoEStatus.NOT_STARTED,
  VoEStatus.IN_PROGRESS,
  VoEStatus.PENDING_REVIEW,
]

// Default outbound applicant stages exclude Not Interested and Disqualified
const DEFAULT_OUTBOUND_APPLICANT_STAGES = [
  ApplicantStage.PROSPECT,
  ApplicantStage.HIRED,
]

const buildEmploymentDatePresets = (periodMonths: number): DatePreset[] => {
  const periodLabel = formatVerificationPeriod(periodMonths)
  return [
    {
      label: `Over ${periodLabel} ago`,
      value: 'overPeriod',
      start: () => dayjs('2000-01-01').toDate(),
      end: () =>
        dayjs()
          .subtract(periodMonths, 'month')
          .startOf('month')
          .subtract(1, 'day')
          .toDate(),
    },
    {
      label: 'Over 5 years ago',
      value: 'over5Years',
      start: () => dayjs('2000-01-01').toDate(),
      end: () =>
        dayjs()
          .subtract(5, 'year')
          .startOf('month')
          .subtract(1, 'day')
          .toDate(),
    },
    {
      label: 'Over 7 years ago',
      value: 'over7Years',
      start: () => dayjs('2000-01-01').toDate(),
      end: () =>
        dayjs()
          .subtract(7, 'year')
          .startOf('month')
          .subtract(1, 'day')
          .toDate(),
    },
    {
      label: 'Over 10 years ago',
      value: 'over10Years',
      start: () => dayjs('2000-01-01').toDate(),
      end: () =>
        dayjs()
          .subtract(10, 'year')
          .startOf('month')
          .subtract(1, 'day')
          .toDate(),
    },
    ...(periodMonths < 60
      ? [
          {
            label: `${periodLabel} to 5 years ago`,
            value: 'periodTo5Years',
            start: () => dayjs().subtract(5, 'year').startOf('month').toDate(),
            end: () =>
              dayjs()
                .subtract(periodMonths, 'month')
                .startOf('month')
                .subtract(1, 'day')
                .toDate(),
          },
        ]
      : []),
    {
      label: `Within last ${periodLabel}`,
      value: 'withinPeriod',
      start: () =>
        dayjs().subtract(periodMonths, 'month').startOf('month').toDate(),
      end: () => new Date(),
    },
  ]
}

// Most actionable first: an answer to review, a known step, a decision, a
// contact to find, policy holds, untouched, then automation's rows.
const DEFAULT_SORT_FIELD = 'NEXT_ACTION'
const DEFAULT_SORT_DIRECTION: 'asc' | 'desc' = 'asc'

const VoERequestsPage = () => {
  const { currentUser } = useAuth()
  const { data: companyData } = useQuery(GET_COMPANY, {
    variables: { id: currentUser?.companyId },
    skip: !currentUser?.companyId,
  })
  const verificationPeriodMonths =
    companyData?.company?.voeVerificationPeriodMonths ??
    DEFAULT_VOE_VERIFICATION_PERIOD_MONTHS

  const employmentDatePresets = buildEmploymentDatePresets(
    verificationPeriodMonths
  )

  const urlFilters = loadFiltersFromUrl()
  const defaultTab = 'outbound'
  const activeTab = urlFilters.tab || defaultTab

  // ── Shared filter state ──────────────────────────────────────────────────
  const [searchText, setSearchText] = useState(urlFilters.searchText)
  const [hiringStageTab, setHiringStageTab] = useState(
    urlFilters.hiringStageTab || 'all'
  )

  // ── Outbound filter state ────────────────────────────────────────────────
  const [selectedOutboundStatuses, setSelectedOutboundStatuses] = useState<
    string[]
  >(
    urlFilters.statuses.length > 0
      ? urlFilters.statuses
      : DEFAULT_OUTBOUND_STATUSES
  )
  const [minContactAttempts, setMinContactAttempts] = useState<number | null>(
    urlFilters.minContactAttempts ?? null
  )
  const [maxContactAttempts, setMaxContactAttempts] = useState<number | null>(
    urlFilters.maxContactAttempts ?? null
  )
  const [lastContactDate, setLastContactDate] = useState<{
    gte: Date | null
    lte: Date | null
  }>({
    gte: urlFilters.lastContactDate?.gte ?? null,
    lte: urlFilters.lastContactDate?.lte ?? null,
  })
  const [employmentStartDate, setEmploymentStartDate] = useState<{
    gte: Date | null
    lte: Date | null
  }>({
    gte: urlFilters.employmentStartDate?.gte ?? null,
    lte: urlFilters.employmentStartDate?.lte ?? null,
  })
  const [employmentEndDate, setEmploymentEndDate] = useState<{
    gte: Date | null
    lte: Date | null
  }>({
    gte: urlFilters.employmentEndDate?.gte ?? null,
    lte: urlFilters.employmentEndDate?.lte ?? null,
  })
  const [selectedAssignee, setSelectedAssignee] = useState<string[]>(
    urlFilters.assigneeIds ?? []
  )
  const [selectedApplicantStages, setSelectedApplicantStages] = useState<
    string[]
  >(
    urlFilters.applicantStages.length > 0
      ? urlFilters.applicantStages
      : DEFAULT_OUTBOUND_APPLICANT_STAGES
  )
  // Column-level filter state (header dropdowns)
  const [lastContactFilters, setLastContactFilters] = useState<string[]>(() => {
    const filters: string[] = []
    if (urlFilters.neverContacted) filters.push('NEVER_CONTACTED')
    // Detect stale filter from URL (lte set, no gte = stale filter)
    if (urlFilters.lastContactDate?.lte && !urlFilters.lastContactDate?.gte)
      filters.push('STALE_7_DAYS')
    return filters
  })
  const [contactInfoFilters, setContactInfoFilters] = useState<string[]>(
    urlFilters.contactInfoFilter ?? []
  )
  const [consentFilters, setConsentFilters] = useState<string[]>([])
  const [responseFilters, setResponseFilters] = useState<string[]>(
    urlFilters.responseFilter ?? []
  )
  const [nextActionFilters, setNextActionFilters] = useState<string[]>(
    urlFilters.nextActionFilter ?? []
  )
  const [empPeriodFilters, setEmpPeriodFilters] = useState<string[]>(() => {
    // Detect expired/within-period filter from URL
    if (urlFilters.employmentEndDate?.lte && !urlFilters.employmentEndDate?.gte)
      return ['EXPIRED']
    if (urlFilters.employmentEndDate?.gte && !urlFilters.employmentEndDate?.lte)
      return ['WITHIN_PERIOD']
    return []
  })
  const [sortField, setSortField] = useState<string>(
    urlFilters.sortField ?? DEFAULT_SORT_FIELD
  )
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    urlFilters.sortDirection ?? DEFAULT_SORT_DIRECTION
  )
  const [assigneeOptions, setAssigneeOptions] = useState<
    { label: string; value: string }[]
  >([])

  // ── Inbound filter state ─────────────────────────────────────────────────
  const [selectedInboundStatuses, setSelectedInboundStatuses] = useState<
    string[]
  >(urlFilters.statuses)

  const [opened, { open, close }] = useDisclosure(false)

  const onError = useCallback(() => {
    toast('Unable to get VoE requests, please try again.', 'error')
  }, [])

  const [loadVoEList, { data: voeList, loading }] = useLazyQuery(GET_VOE_LIST, {
    onError,
  })

  const [loadTabCounts, { data: tabCountsData }] =
    useLazyQuery(GET_VOE_TAB_COUNTS)

  const tabCounts = tabCountsData
    ? {
        all: tabCountsData.all?.totalCount ?? null,
        hired: tabCountsData.hired?.totalCount ?? null,
        onboarding: tabCountsData.onboarding?.totalCount ?? null,
        qualification: tabCountsData.qualification?.totalCount ?? null,
        application: tabCountsData.application?.totalCount ?? null,
        lead: tabCountsData.lead?.totalCount ?? null,
      }
    : null

  const [loadEmployees] = useLazyQuery(GET_EMPLOYEES_ONLY, {
    variables: { pageInfo: { offset: 0, limit: 100 } },
    onCompleted: (data) => {
      const options =
        data?.employees?.items?.map((e) => ({
          label: `${e.firstName} ${e.lastName}`,
          value: e.employeeId,
        })) || []
      setAssigneeOptions(options)
    },
  })

  useEffect(() => {
    loadEmployees()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [sendIntructionsEmail, { loading: loadingInstructionsEmail }] =
    useMutation(SEND_INSTRUCTIONS_EMAIL, {
      onCompleted: () => {
        close()
        toast('Email sent', 'success')
      },
      onError: () => {
        toast('Unable to send email, please try again.', 'error')
      },
    })

  const buildOutboundBaseFilters = useCallback(
    (filters: ReturnType<typeof loadFiltersFromUrl>) => {
      return {
        type: VoEType.OUTBOUND,
        searchText: filters.searchText || undefined,
        statuses: filters.statuses.length
          ? filters.statuses
          : DEFAULT_OUTBOUND_STATUSES,
        assigneeIds: filters.assigneeIds?.length
          ? filters.assigneeIds
          : undefined,
        minContactAttempts: filters.minContactAttempts ?? undefined,
        maxContactAttempts: filters.maxContactAttempts ?? undefined,
        lastContactDate:
          filters.lastContactDate?.gte || filters.lastContactDate?.lte
            ? filters.lastContactDate
            : undefined,
        applicantStages: filters.applicantStages.length
          ? filters.applicantStages
          : DEFAULT_OUTBOUND_APPLICANT_STAGES,
        employmentStartDate:
          filters.employmentStartDate?.gte || filters.employmentStartDate?.lte
            ? filters.employmentStartDate
            : undefined,
        employmentEndDate:
          filters.employmentEndDate?.gte || filters.employmentEndDate?.lte
            ? filters.employmentEndDate
            : undefined,
        contactInfoFilter: filters.contactInfoFilter?.length
          ? filters.contactInfoFilter
          : undefined,
        consentFilter: filters.consentFilter?.length
          ? filters.consentFilter
          : undefined,
        ...buildResponseAutonomyFilters(filters.responseFilter),
        ...buildNextActionFilters(filters.nextActionFilter),
        neverContacted: filters.neverContacted || undefined,
      }
    },
    []
  )

  const loadOutboundTabCounts = useCallback(
    (filters: ReturnType<typeof loadFiltersFromUrl>) => {
      const base = buildOutboundBaseFilters(filters)
      loadTabCounts({
        variables: {
          allFilters: base,
          hiredFilters: { ...base, applicantStages: [ApplicantStage.HIRED] },
          onboardingFilters: {
            ...base,
            applicantStages: [ApplicantStage.PROSPECT],
            hiringStages: [HiringStage.ONBOARDING],
          },
          qualificationFilters: {
            ...base,
            applicantStages: [ApplicantStage.PROSPECT],
            hiringStages: [HiringStage.QUALIFICATION],
          },
          applicationFilters: {
            ...base,
            applicantStages: [ApplicantStage.PROSPECT],
            hiringStages: [HiringStage.APPLICATION],
          },
          leadFilters: {
            ...base,
            applicantStages: [ApplicantStage.PROSPECT],
            hiringStages: [HiringStage.LEAD],
          },
        },
      })
    },
    [buildOutboundBaseFilters, loadTabCounts]
  )

  const onLoadPage = useCallback(
    ({ offset, limit }: { offset: number; limit: number }) => {
      const filters = loadFiltersFromUrl()
      const tab = filters.tab || defaultTab

      if (tab === 'outbound') {
        const activeSortField = filters.sortField ?? DEFAULT_SORT_FIELD
        const activeSortDirection =
          filters.sortDirection ?? DEFAULT_SORT_DIRECTION
        const baseFilters = buildOutboundBaseFilters(filters)
        loadVoEList({
          variables: {
            filters: {
              ...baseFilters,
              ...(filters.hiringStageTab && filters.hiringStageTab !== 'all'
                ? filters.hiringStageTab === 'hired'
                  ? { applicantStages: [ApplicantStage.HIRED] }
                  : {
                      applicantStages: [ApplicantStage.PROSPECT],
                      hiringStages: [filters.hiringStageTab.toUpperCase()],
                    }
                : {}),
              orderBy: {
                field: activeSortField,
                direction: activeSortDirection,
              },
            },
            pageInfo: { offset, limit },
          },
        })
        loadOutboundTabCounts(filters)
      } else {
        loadVoEList({
          variables: {
            filters: {
              type: VoEType.INBOUND,
              searchText: filters.searchText || undefined,
              statuses: filters.statuses.length ? filters.statuses : undefined,
              createdAt: filters.createdAt,
              orderBy: {
                field: 'CREATED_AT',
                direction: 'desc',
              },
            },
            pageInfo: { offset, limit },
          },
        })
      }
    },
    [loadVoEList, buildOutboundBaseFilters, loadOutboundTabCounts]
  )

  const urlParams = useParams()
  useEffect(() => {
    onLoadPage({ offset: 0, limit: LIMIT })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams])

  const handleTabChange = (tab: string) => {
    setSearchText('')
    setSelectedOutboundStatuses(DEFAULT_OUTBOUND_STATUSES)
    setMinContactAttempts(null)
    setMaxContactAttempts(null)
    setLastContactDate({ gte: null, lte: null })
    setEmploymentStartDate({ gte: null, lte: null })
    setEmploymentEndDate({ gte: null, lte: null })
    setSelectedAssignee([])
    setSelectedApplicantStages(DEFAULT_OUTBOUND_APPLICANT_STAGES)
    setLastContactFilters([])
    setContactInfoFilters([])
    setEmpPeriodFilters([])
    setResponseFilters([])
    setNextActionFilters([])
    setSortField(DEFAULT_SORT_FIELD)
    setSortDirection(DEFAULT_SORT_DIRECTION)
    setSelectedInboundStatuses([])
    setHiringStageTab('all')

    updateFiltersInUrl({
      tab,
      statuses: [],
      searchText: '',
      createdAt: { gte: null, lte: null },
      assigneeIds: [],
      minContactAttempts: null,
      maxContactAttempts: null,
      lastContactDate: { gte: null, lte: null },
      employmentStartDate: { gte: null, lte: null },
      employmentEndDate: { gte: null, lte: null },
      applicantStages: [],
      contactInfoFilter: [],
      consentFilter: [],
      responseFilter: [],
      nextActionFilter: [],
      neverContacted: false,
      sortField: null,
      sortDirection: null,
      hiringStageTab: null,
    })
  }

  const handleHiringStageTabChange = (tab: string) => {
    setHiringStageTab(tab)
    const filters = loadFiltersFromUrl()
    updateFiltersInUrl({
      ...filters,
      hiringStageTab: tab === 'all' ? null : tab,
    })
  }

  const handleSortChange = (field: string, direction: 'asc' | 'desc') => {
    setSortField(field)
    setSortDirection(direction)
  }

  // Outbound filter handlers
  useEffect(() => {
    const filters = loadFiltersFromUrl()
    if ((filters.tab || defaultTab) !== 'outbound') return
    updateFiltersInUrl({ ...filters, statuses: selectedOutboundStatuses })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOutboundStatuses])

  useEffect(() => {
    updateFiltersInUrl({
      ...loadFiltersFromUrl(),
      minContactAttempts,
      maxContactAttempts,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minContactAttempts, maxContactAttempts])

  useEffect(() => {
    updateFiltersInUrl({
      ...loadFiltersFromUrl(),
      lastContactDate,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastContactDate])

  useEffect(() => {
    updateFiltersInUrl({
      ...loadFiltersFromUrl(),
      employmentStartDate,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employmentStartDate])

  useEffect(() => {
    updateFiltersInUrl({
      ...loadFiltersFromUrl(),
      employmentEndDate,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employmentEndDate])

  useEffect(() => {
    updateFiltersInUrl({
      ...loadFiltersFromUrl(),
      assigneeIds: selectedAssignee,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssignee])

  useEffect(() => {
    const filters = loadFiltersFromUrl()
    if ((filters.tab || defaultTab) !== 'outbound') return
    updateFiltersInUrl({ ...filters, applicantStages: selectedApplicantStages })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApplicantStages])

  // Sync column-level Last Contact filters → query state
  useEffect(() => {
    const filters = loadFiltersFromUrl()
    if ((filters.tab || defaultTab) !== 'outbound') return

    const hasNever = lastContactFilters.includes('NEVER_CONTACTED')
    const hasStale = lastContactFilters.includes('STALE_7_DAYS')

    // Update neverContacted
    const newNeverContacted = hasNever
    // Update lastContactDate for stale
    const newLastContactDate = hasStale
      ? { gte: null, lte: dayjs().subtract(7, 'day').toDate() }
      : { gte: null as Date | null, lte: null as Date | null }

    setLastContactDate(newLastContactDate)
    updateFiltersInUrl({
      ...filters,
      neverContacted: newNeverContacted,
      lastContactDate: newLastContactDate,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastContactFilters])

  // Sync column-level Contact Info filters → query state
  useEffect(() => {
    const filters = loadFiltersFromUrl()
    if ((filters.tab || defaultTab) !== 'outbound') return
    updateFiltersInUrl({ ...filters, contactInfoFilter: contactInfoFilters })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactInfoFilters])

  // Sync column-level Consent filters → query state
  useEffect(() => {
    const filters = loadFiltersFromUrl()
    if ((filters.tab || defaultTab) !== 'outbound') return
    updateFiltersInUrl({ ...filters, consentFilter: consentFilters })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentFilters])

  // Sync column-level Response filters → query state
  useEffect(() => {
    const filters = loadFiltersFromUrl()
    if ((filters.tab || defaultTab) !== 'outbound') return
    updateFiltersInUrl({ ...filters, responseFilter: responseFilters })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseFilters])

  // Sync column-level Next action filters → query state
  useEffect(() => {
    const filters = loadFiltersFromUrl()
    if ((filters.tab || defaultTab) !== 'outbound') return
    updateFiltersInUrl({ ...filters, nextActionFilter: nextActionFilters })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextActionFilters])

  // Sync column-level Employment Period filters → query state
  useEffect(() => {
    const filters = loadFiltersFromUrl()
    if ((filters.tab || defaultTab) !== 'outbound') return

    if (
      empPeriodFilters.includes('EXPIRED') &&
      empPeriodFilters.includes('WITHIN_PERIOD')
    ) {
      // Both selected = no filter (they cover the entire range)
      setEmploymentEndDate({ gte: null, lte: null })
      updateFiltersInUrl({
        ...filters,
        employmentEndDate: { gte: null, lte: null },
      })
    } else if (empPeriodFilters.includes('EXPIRED')) {
      const lte = dayjs()
        .subtract(verificationPeriodMonths, 'month')
        .startOf('month')
        .subtract(1, 'day')
        .toDate()
      setEmploymentEndDate({ gte: null, lte })
      updateFiltersInUrl({ ...filters, employmentEndDate: { gte: null, lte } })
    } else if (empPeriodFilters.includes('WITHIN_PERIOD')) {
      const gte = dayjs()
        .subtract(verificationPeriodMonths, 'month')
        .startOf('month')
        .toDate()
      setEmploymentEndDate({ gte, lte: null })
      updateFiltersInUrl({ ...filters, employmentEndDate: { gte, lte: null } })
    } else {
      setEmploymentEndDate({ gte: null, lte: null })
      updateFiltersInUrl({
        ...filters,
        employmentEndDate: { gte: null, lte: null },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empPeriodFilters, verificationPeriodMonths])

  // Only a sort the user chose goes in the URL: a link that carries no sort
  // follows whatever the default is, today and after it changes.
  useEffect(() => {
    const filters = loadFiltersFromUrl()
    if ((filters.tab || defaultTab) !== 'outbound') return
    const isDefault =
      sortField === DEFAULT_SORT_FIELD &&
      sortDirection === DEFAULT_SORT_DIRECTION
    updateFiltersInUrl({
      ...filters,
      sortField: isDefault ? null : sortField,
      sortDirection: isDefault ? null : sortDirection,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection])

  // Inbound filter handlers
  useEffect(() => {
    const filters = loadFiltersFromUrl()
    if ((filters.tab || defaultTab) !== 'inbound') return
    updateFiltersInUrl({ ...filters, statuses: selectedInboundStatuses })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInboundStatuses])

  const onDateRangeChange = (value: DateRangeValue) => {
    const filters = loadFiltersFromUrl()
    updateFiltersInUrl({
      ...filters,
      createdAt: {
        ...filters.createdAt,
        gte: value.gte ?? null,
        lte: value.lte ?? null,
      },
    })
  }

  const onSearch = (text: string) => {
    updateFiltersInUrl({ ...loadFiltersFromUrl(), searchText: text })
  }

  const onSendEmail = ({ email }) => {
    sendIntructionsEmail({ variables: { emailTo: email } })
  }

  const firstName = currentUser?.firstName
  const lastName = currentUser?.lastName
  const fullName = `${firstName} ${lastName}`
  const emailAddress = currentUser?.email
  const phoneNumber = currentUser?.phone
  const companyName = currentUser?.companyName
  const companyId = currentUser?.companyId

  const emailBody = `Hello,

Thank you for reaching out to ${companyName}. Please follow the instructions below to submit any employment verification requests you may have for drivers who have previously been part of our team.

To request verification for an individual's employment with us, please follow the steps below:
  •  Access our secure Employment Verification Request portal via this website: https://dashboard.getdoublenickel.com/external-voe-request/${companyId}
  •  Fill in the required fields with the applicant's information. Ensure all information is accurate; if our records do not match your request we will not be able to verify prior employment.
  •  Submit the request, and our team will process the request and provide a timely response.

We aim to provide prompt and accurate information to support your verification process. Should there be any additional information or specific concerns you wish to address, feel free to contact us directly at ${emailAddress} or ${phoneNumber}.

We appreciate your cooperation and look forward to assisting you.

Best regards,
${companyName}`

  const tabOptions: ToggleOption[] = [
    { value: 'outbound', label: 'Outgoing' },
    { value: 'inbound', label: 'Incoming' },
  ]

  const outboundFilters: FiltersMenuFilter[] = [
    {
      title: 'Status',
      selected: selectedOutboundStatuses,
      onChange: setSelectedOutboundStatuses,
      options: ALL_VOE_STATUSES,
      alphabetize: false,
    },
    {
      title: 'Applicant Stage',
      selected: selectedApplicantStages,
      onChange: setSelectedApplicantStages,
      options: APPLICANT_STAGE_OPTIONS,
      alphabetize: false,
    },
    {
      title: 'Assigned To',
      selected: selectedAssignee,
      onChange: setSelectedAssignee,
      options: assigneeOptions,
    },
    {
      title: 'Next action',
      selected: nextActionFilters,
      onChange: setNextActionFilters,
      options: NEXT_ACTION_FILTER_OPTIONS,
      alphabetize: false,
    },
    {
      title: 'Response',
      selected: responseFilters,
      onChange: setResponseFilters,
      options: RESPONSE_FILTER_OPTIONS,
      alphabetize: false,
    },
    {
      type: 'range',
      title: 'Contact Attempts',
      minValue: minContactAttempts,
      maxValue: maxContactAttempts,
      min: 0,
      onChange: (min, max) => {
        setMinContactAttempts(min)
        setMaxContactAttempts(max)
      },
    },
    {
      type: 'daterange',
      title: 'Last Attempt',
      value: {
        gte: lastContactDate.gte,
        lte: lastContactDate.lte,
      },
      onChange: (value) => {
        setLastContactDate({ gte: value.gte ?? null, lte: value.lte ?? null })
        setLastContactFilters([])
      },
    },
    {
      type: 'daterange',
      title: 'Employment Start Date',
      value: {
        gte: employmentStartDate.gte,
        lte: employmentStartDate.lte,
      },
      onChange: (value) =>
        setEmploymentStartDate({
          gte: value.gte ?? null,
          lte: value.lte ?? null,
        }),
      presets: employmentDatePresets,
    },
    {
      type: 'daterange',
      title: 'Employment End Date',
      value: {
        gte: employmentEndDate.gte,
        lte: employmentEndDate.lte,
      },
      onChange: (value) => {
        setEmploymentEndDate({ gte: value.gte ?? null, lte: value.lte ?? null })
        setEmpPeriodFilters([])
      },
      presets: employmentDatePresets,
    },
  ]

  const inboundFilters: FiltersMenuFilter[] = [
    {
      title: 'Status',
      selected: selectedInboundStatuses,
      onChange: setSelectedInboundStatuses,
      options: INBOUND_STATUSES,
      alphabetize: false,
    },
    {
      type: 'daterange',
      title: 'Request Date',
      value: {
        gte: urlFilters.createdAt?.gte ?? null,
        lte: urlFilters.createdAt?.lte ?? null,
      },
      onChange: onDateRangeChange,
    },
  ]

  const activeFilters =
    activeTab === 'outbound' ? outboundFilters : inboundFilters
  const searchPlaceholder =
    activeTab === 'outbound'
      ? 'Search by name, company, phone, DOT…'
      : 'Search by driver name or company name…'

  const toolbarControls = (
    <div className="flex flex-row items-center gap-3">
      <div className="w-64">
        <SearchBox
          value={searchText}
          placeholder={searchPlaceholder}
          onSearch={onSearch}
          setValue={setSearchText}
        />
      </div>
      <FiltersMenu filters={activeFilters} />
      <ActionsMenu
        text="Actions"
        rightIcon={
          <IconChevronDown className="h-4 w-4 fill-none stroke-white" />
        }
        items={[
          {
            label: (
              <div className="flex flex-row items-center gap-2 text-sm">
                <Link size={12} className="stroke-doubleNickel-gray-700" />
                Copy VoE request link
              </div>
            ),
            onClick: () => {
              navigator.clipboard.writeText(
                `${window.location.origin}/external-voe-request/${companyId}`
              )
              toast('Copied to clipboard', 'success')
            },
          },
          {
            label: (
              <div className="flex flex-row items-center gap-2 text-sm">
                <Send size={12} className="stroke-doubleNickel-gray-700" />
                Send instructions
              </div>
            ),
            onClick: () => open(),
          },
        ]}
      />
    </div>
  )

  return (
    <div className="flex h-full w-full flex-col">
      <MetaTags
        title="Employment Verifications"
        description="Employment Verifications"
      />
      <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 pb-3">
        <div className="flex flex-row items-center justify-between pt-1">
          <div className="text-lg font-semibold text-doubleNickel-gray-900">
            Employment Verifications
          </div>
          <ToggleButtonGroup
            options={tabOptions}
            value={activeTab}
            onChange={handleTabChange}
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          {/* ── Outbound panel ───────────────────────────────────────── */}
          {activeTab === 'outbound' && (
            <div className="min-h-0 flex-1">
              <OutboundVoETable
                loading={loading}
                voeList={voeList?.verificationOfEmployments.items}
                totalCount={voeList?.verificationOfEmployments.totalCount || 0}
                onLoadPage={onLoadPage}
                sortField={sortField}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
                hiringStageTab={hiringStageTab}
                onHiringStageTabChange={handleHiringStageTabChange}
                tabCounts={tabCounts}
                toolbarControls={toolbarControls}
                verificationPeriodMonths={verificationPeriodMonths}
                lastContactFilters={lastContactFilters}
                onLastContactFiltersChange={setLastContactFilters}
                contactInfoFilters={contactInfoFilters}
                onContactInfoFiltersChange={setContactInfoFilters}
                empPeriodFilters={empPeriodFilters}
                onEmpPeriodFiltersChange={setEmpPeriodFilters}
                consentFilters={consentFilters}
                onConsentFiltersChange={setConsentFilters}
                nextActionFilters={nextActionFilters}
                onNextActionFiltersChange={setNextActionFilters}
                companyPhoneNumber={
                  formatInternationalPhoneNumber(
                    companyData?.company?.companyPhoneNumber ||
                      companyData?.company?.phoneNumber
                  ) ?? ''
                }
              />
            </div>
          )}

          {/* ── Inbound panel ────────────────────────────────────────── */}
          {activeTab === 'inbound' && (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex items-center justify-end border-b px-2 pb-2">
                {toolbarControls}
              </div>
              <VoERequestsTable
                loading={loading}
                voeList={voeList?.verificationOfEmployments.items}
                totalCount={voeList?.verificationOfEmployments.totalCount || 0}
                onLoadPage={onLoadPage}
              />
            </div>
          )}
        </div>
      </div>

      <EmailSenderModal
        from={currentUser?.email}
        title="Send instructions via Email"
        subject={`Instructions for Requesting Employment Verification from ${fullName}`}
        body={emailBody}
        isOpen={opened}
        onClose={close}
        loading={loadingInstructionsEmail}
        onSend={onSendEmail}
      />
    </div>
  )
}

export default VoERequestsPage
